import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const WORDS = ["ARCADE", "PIXEL", "PLAYER", "LEVEL", "SCORE", "POWER", "QUEST", "NINJA", "ROBOT", "LASER", "BONUS", "DRAGON", "WIZARD", "ROCKET", "CASTLE"];
const scramble = (w) => { let a = w.split(""); do { a.sort(() => Math.random() - 0.5); } while (a.join("") === w); return a.join(""); };

export function WordScramble({ onScore }) {
  const [word, setWord] = useState("");
  const [shown, setShown] = useState("");
  const [input, setInput] = useState("");
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(60);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [flash, setFlash] = useState(null);
  const reported = useRef(false);

  const nextWord = () => { const w = WORDS[Math.floor(Math.random() * WORDS.length)]; setWord(w); setShown(scramble(w)); setInput(""); };
  const start = () => { setScore(0); setTime(60); setRunning(true); setOver(false); nextWord(); reported.current = false; };

  useEffect(() => {
    if (!running) return;
    if (time <= 0) { setRunning(false); setOver(true); return; }
    const t = setTimeout(() => setTime((x) => x - 1), 1000);
    return () => clearTimeout(t);
  }, [running, time]);

  const submit = () => {
    if (!running) return;
    if (input.toUpperCase() === word) { setScore((s) => s + 1); setFlash("ok"); nextWord(); }
    else { setFlash("no"); }
    setTimeout(() => setFlash(null), 200);
  };

  useEffect(() => { if (over && !reported.current) { reported.current = true; onScore?.(score * 60); } }, [over, score, onScore]);

  return (
    <div className="flex flex-col items-center w-full max-w-sm text-center" data-testid="word-scramble">
      <div className="flex gap-6 mb-4 font-mono">
        <span className="text-neon-cyan text-lg" data-testid="ws-score">SOLVED: {score}</span>
        <span className={`text-lg ${time <= 10 ? "text-neon-pink animate-blink" : "text-neon-yellow"}`}>⏱ {time}s</span>
      </div>
      {!running ? (
        <>
          {over && <p className="font-display font-black text-2xl text-neon-lime uppercase mb-3" data-testid="ws-result">Time! Solved {score}</p>}
          <Button onClick={start} data-testid="ws-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase h-12 px-8">
            <Play className="w-4 h-4 mr-2 fill-black" /> {over ? "Play Again" : "Start"}
          </Button>
        </>
      ) : (
        <>
          <p className={`font-display font-black text-4xl tracking-[0.3em] mb-5 ${flash === "no" ? "text-neon-pink" : "text-neon-yellow"}`} data-testid="ws-word">{shown}</p>
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="ws-input" autoFocus
            className="w-full bg-zinc-950 border-2 border-neon-cyan rounded-lg px-4 py-3 text-center font-display font-bold text-2xl uppercase text-white outline-none mb-3" placeholder="unscramble" />
          <Button onClick={submit} data-testid="ws-submit" className="bg-neon-pink text-white rounded-none font-bold uppercase">Submit</Button>
        </>
      )}
      <p className="text-zinc-500 text-xs font-mono mt-4">Unscramble as many gaming words as you can in 60 seconds!</p>
    </div>
  );
}
